import { useCallback, useMemo, useState } from 'react';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import useAsyncValue from '../../../../hooks/useAsyncValue';
import useOnMount from '../../../../hooks/useOnMount';
import useRequestConnectedToAsyncValue from '../../../../hooks/useRequestConnectedToAsyncValue';
import { get, post, put, remove } from '../../../../redux/api';
import type { Policy } from '../../../../../shared/model-typings/policy';
import type {
  APITokenCreateRequest,
  APITokenResponse,
  APITokenUpdateRequest,
} from '../../../../../shared/settings/apiTokens';
import type { FailedFetchResponse } from '../../../../helpers/errors';

type APITokenSettings = {
  onCreateToken: (token: APITokenCreateRequest) => Promise<string | null>;
  onUpdateToken: (tokenId: number, token: APITokenUpdateRequest) => Promise<void>;
  onDeleteToken: (id: number) => void;
  apiTokens: APITokenResponse[];
  loadingState: {
    isReading: boolean;
    isMutating: boolean;
  }
  errorState: {
    readErrors: string[] | null;
    mutateErrors: string[] | null;
  }
};

type FetchRequest = {
  apiTokens: APITokenResponse[];
  policies: Policy[];
};

/**
 * Hook for fetching the API Token settings for a given org
 */
function useAPITokens(): APITokenSettings {
  const createTokenEndpoint = useMemo(() =>
    `/api/user/${window.user?.id ?? 0}/api_token`, []);

  //  Memoized because useAsyncRequest will infinite loop if we don't.
  const fetchRequest = useCallback(async () => {
    const { data: apiTokens } = await get<APITokenResponse[]>(`/api/organizations/${window.user?.organizationId ?? 0}/api-tokens`);

    return {
      data: {
        apiTokens,
        policies,
      } as FetchRequest,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const asyncVal = useAsyncValue<FetchRequest>();
  const onFetchTokens = useRequestConnectedToAsyncValue(fetchRequest, asyncVal);

  // call this once on mount
  useOnMount(onFetchTokens);

  const { value, isLoading: isReading, errors } = asyncVal;

  const policies = useMemo(() => value?.policies || [], [value?.policies]);
  const apiTokens = useMemo(() => value?.apiTokens || [], [value?.apiTokens]);

  const [isMutating, setIsMutating] = useState(false);
  const [mutateErrors, setMutateErrors] = useState<string[] | null>(null);
  const onAddToast = useAddToast();

  // Utility fn to handle loading/error states for doing data mutation
  const handleUpdate = useCallback(async<T>(
    handler: () => Promise<null | T>,
    onSuccessToast?: () => void,
  ) => {
    let dataToReturn: T | null = null;
    try {
      setIsMutating(true);
      setMutateErrors([]);
      dataToReturn = await handler();
      // refetch list of API tokens after mutation is done
      await onFetchTokens();
      onSuccessToast?.();
    } catch (error: unknown) {
      setMutateErrors(errorMessagesListFromResponse(error as FailedFetchResponse));
    } finally {
      setIsMutating(false);
    }

    return dataToReturn;
  }, [onFetchTokens]);

  const onCreateToken = useCallback(async (token: APITokenCreateRequest): Promise<string | null> => {
    const creationFn = async () => {
      const { data } = await post<APITokenCreateRequest, {token: string}>(
        createTokenEndpoint,
        token,
      );

      return data;
    };

    const data = await handleUpdate(creationFn);

    if (!data?.token) {
      setMutateErrors(['Error creating token.']);
      return null;
    }

    return data.token;
  }, [createTokenEndpoint, handleUpdate]);

  const onUpdateToken = useCallback(async (tokenId: number, token: APITokenUpdateRequest) => {
    await handleUpdate(
      () => put(
        `/api/user/${window.user?.id || 0}/api_token/${tokenId}`,
        {
          name: token.name,
          meta: {
            ...token.meta,
          },
        },
      ),
      () => onAddToast({
        type: 'success',
        content: `Successfully updated token ${token.name}`,
      }),
    );
  }, [handleUpdate, onAddToast]);

  const { onConfirm } = useDialogContext();

  const onDeleteToken = useCallback((tokenId: number) => {
    onConfirm({
      title: 'Confirm API Token removal',
      body: 'Are you sure you want to delete this API token? This cannot be undone.',
      onConfirm: async () => {
        await handleUpdate(
          () => remove(`/api/organizations/${window.user?.organizationId || 0}/api-tokens/${tokenId}`),
          () => onAddToast({
            type: 'success',
            content: `Successfully deleted token`,
          }),
        );
      },
    });
  }, [handleUpdate, onConfirm, onAddToast]);

  return {
    // mutation functions
    onCreateToken,
    onUpdateToken,
    onDeleteToken,
    // initial data for loading
    apiTokens,
    // loading states
    loadingState: {
      isReading,
      isMutating,
    },
    // error states
    errorState: {
      readErrors: errors,
      mutateErrors,
    },
  };
}

export default useAPITokens;
